import {
  DocumentCreatedDelegate,
  DocumentRequestsViewModelPort,
  RequestedDocumentDescriptor,
} from '../documents/components/DocumentRequests/DocumentRequestsViewModel'
import DocumentStore from 'documents/DocumentStore'
import Document from '../documents/Document'
import Job from './Job'
import { DocumentPopulate } from '../documents/dtos/document'
import { computed, runInAction, makeObservable } from 'mobx'
import DocumentVersionStore from '../documents/DocumentVersionStore'
import { Id } from '../types/definitions'
import { CancellationToken } from 'infra/cancellation'
import { DocumentPickerPorts } from '../documents/views/DocumentPicker/DocumentPickerViewModel'
import DocumentAccessStore from '../documents/DocumentAccessStore'
import { GetCurrentMember } from '../iam/SessionStore'

/**
 * Adapter for linking Documents to a Job.
 */
export class JobDocumentsLinkingAdapter {
  /**
   * Used for the Document Requests VM (for Corrections).
   */
  documentRequestsAdapter: DocumentRequestsViewModelPort = {
    fetchDocument: this.fetchDocument.bind(this),
    uploadNewDocument: this.uploadNewDocument.bind(this),
    scrollProvider: this.scrollProvider.bind(this),
    documents: this.getDocuments.bind(this),
    linkDocument: this.linkDocumentToJob.bind(this),
    canLinkDocument: (doc) =>
      this.canLinkDocumentToJob(doc, LinkingContext.DocumentRequests),
    downloadDocument: this.downloadDocument.bind(this),
  }

  /**
   * Used for the Link Document to Job use case.
   */
  documentPickerAdapter: DocumentPickerPorts = {
    isSelected: this.isDocumentLinkedToJob.bind(this),
    onSelect: this.linkDocumentToJob.bind(this),
    scrollProvider: this.scrollProvider.bind(this),
    documents: this.getDocuments.bind(this),
    canLinkDocument: (doc) =>
      this.canLinkDocumentToJob(doc, LinkingContext.LinkToJob),
  }

  /**
   * Constructor.
   */
  constructor(
    private job: Job,
    private documentStore: DocumentStore,
    private documentVersionStore: DocumentVersionStore,
    private documentAccessStore: DocumentAccessStore,
    private getCurrentMember: GetCurrentMember,
  ) {
    makeObservable(this)
  }

  /**
   * Getter for the current member in the workspace that the job is in.
   */
  @computed
  private get currentMember() {
    return this.getCurrentMember(this.job.workspace.id)
  }

  @computed
  private get documentsLinkedToJob() {
    return this.documentStore.forJob(Id.assert(this.job.id))
  }

  /**
   * Getter for user documents.
   * @private
   */
  private getDocuments(): Document[] {
    return this.documentStore.forMember(this.currentMember)
  }

  /**
   * Provides a scroller for the User Documents
   */
  private scrollProvider() {
    return this.documentStore.userDocumentsScrollProviderForWorkspace(
      this.job.workspace.id,
    )
  }

  /**
   * Whether we can link the document to the job depending on the context.
   *
   * @param doc
   * @param context
   * @private
   */
  private canLinkDocumentToJob(
    doc: Document,
    context: LinkingContext,
  ): boolean {
    if (doc.archived) {
      return false
    }
    if (
      this.documentsLinkedToJob.includes(doc) &&
      context === LinkingContext.LinkToJob
    ) {
      return false
    }
    return doc.workspaceId === this.job.workspace.id
  }

  private isDocumentLinkedToJob(doc: Document): boolean {
    return this.documentsLinkedToJob.includes(doc)
  }

  /**
   * Used by the Document Picker for linking docs.
   *
   * @param doc
   */
  private async linkDocumentToJob(doc: Document): Promise<void> {
    const jobId = Id.assert(this.job.id)
    const access = doc.accesses.find(
      (a) => a.type === 'Job' && a.jobId === jobId,
    )
    if (!access) {
      await this.documentAccessStore.createAccess(Id.assert(doc.id), {
        type: 'Job',
        job_id: jobId,
      })
    }
  }

  private fetchDocument(
    id: Id,
    populate: DocumentPopulate,
  ): Promise<Document | null> {
    return this.documentStore.fetchDocument(id, populate)
  }

  private async uploadNewDocument(
    descriptor: RequestedDocumentDescriptor,
    file: File,
    onDocumentCreated: DocumentCreatedDelegate,
    cancellationToken: CancellationToken,
  ): Promise<void> {
    const doc = await this.documentStore.createDocument({
      workspace_id: Id.assert(this.job.workspace.id),
      title: descriptor.title,
      tags: descriptor.documentTagIds,
      access: {
        type: 'Job' as const,
        job_id: Id.assert(this.job.id),
      },
    })

    if (cancellationToken.isCancelled) {
      // It was cancelled, archive the doc.
      await this.documentStore.archiveDocument(Id.assert(doc.id))
      return
    }

    // Adds the new doc to the list.
    const scrollProvider = this.scrollProvider()
    runInAction(() => {
      scrollProvider.items.push(doc)
    })

    onDocumentCreated(doc)
    await this.documentVersionStore.createVersion({
      action: { type: 'ReplaceContents', files: [file] },
      message: 'Initial version',
      document_id: Id.assert(doc.id),
    })
  }

  downloadDocument(document: Document, inline = false) {
    this.documentStore.download(document, inline)
  }
}

/**
 * Used to control the behavior of the document linking experience.
 */
enum LinkingContext {
  /**
   * This mode will allow docs already linked to show up in the list.
   */
  DocumentRequests,
  /**
   * This mode prevents documents already linked from showing up in the list.
   */
  LinkToJob,
}
